<template>
    <div class="space-y-2">
        <div class="flex items-center justify-between">
            <div class="text-2xl font-medium text-gray-900">
                {{ t('purchaseContract.customerInformation') }}
            </div>

            <prime-button
                v-if="!editing"
                text
                icon="pi pi-pencil"
                :label="t('edit')"
                :disabled="isProcessing"
                @click="$emit('update:editing', true)"
            />
        </div>

        <div
            v-if="!editing"
            class="grid grid-cols-2 gap-x-6 gap-y-2 text-lg md:gap-x-12"
        >
            <div>
                <div class="text-sm font-medium text-gray-500">
                    {{ t('benefits.name') }}
                </div>
                <div class="text-gray-900">
                    {{ displayName }}
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-gray-500">
                    {{ t('customerInformation.dateOfBirth') }}
                </div>
                <div v-if="dateOfBirth">
                    {{ formatTimelessDate(parseTimeless(dateOfBirth)) }}
                </div>
                <div
                    v-else
                    class="text-gray-300"
                >
                    &mdash;
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-gray-500">
                    {{ t('customerInformation.mobilePhone') }}
                </div>
                <div v-if="mobilePhoneInitial">
                    {{ mobilePhoneInitial }}
                </div>
                <div
                    v-else
                    class="text-gray-300"
                >
                    &mdash;
                </div>
            </div>

            <div>
                <div class="text-sm font-medium text-gray-500">
                    {{ t('customerInformation.emailAddress') }}
                </div>
                <div>
                    {{ email }}
                </div>
            </div>
        </div>

        <div
            v-if="editing"
            class="grid grid-cols-12 gap-x-4 gap-y-1"
        >
            <!-- Name -->

            <div
                class="col-span-12"
                :class="useMiddleName ? 'lg:col-span-4' : 'lg:col-span-6'"
            >
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span
                    >{{ t('customerInformation.firstName') }}
                </div>
                <input-text
                    id="given_name"
                    v-model="firstName"
                    autocomplete="given-name"
                    :placeholder="t('customerInformation.firstName')"
                    class="w-full"
                    :disabled="isProcessing"
                    :pt="{
                        root: {
                            class:
                                showErrors && errorFirstName
                                    ? 'border-red-600'
                                    : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="showErrors"
                    class="text-red-600 text-sm"
                >
                    {{ errorFirstName || '&nbsp;' }}
                </div>
            </div>
            <div
                v-if="useMiddleName"
                class="col-span-12 lg:col-span-4"
            >
                <div class="text-sm text-gray-500">
                    {{ t('customerInformation.middleName') }}
                </div>
                <input-text
                    id="middle_name"
                    v-model="middleName"
                    autocomplete="additional-name"
                    :placeholder="t('customerInformation.middleName')"
                    class="w-full"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorMiddleName || '&nbsp;' }}
                </div>
            </div>
            <div
                class="col-span-12"
                :class="useMiddleName ? 'lg:col-span-4' : 'lg:col-span-6'"
            >
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span
                    >{{ t('customerInformation.lastName') }}
                </div>
                <input-text
                    id="last_name"
                    v-model="lastName"
                    autocomplete="last-name"
                    :placeholder="t('customerInformation.lastName')"
                    class="w-full"
                    :disabled="isProcessing"
                    :pt="{
                        root: {
                            class:
                                showErrors && errorLastName
                                    ? 'border-red-600'
                                    : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="showErrors"
                    class="text-red-600 text-sm"
                >
                    {{ errorLastName || '&nbsp;' }}
                </div>
            </div>

            <!-- Date of Birth -->

            <div class="col-span-12 lg:col-span-5">
                <div class="text-sm text-gray-500">
                    {{ t('customerInformation.dateOfBirth') }}
                </div>
                <dropdown-date-input
                    v-model:date="dateOfBirth"
                    :show-errors="false"
                    :disabled="isProcessing"
                />
            </div>

            <!-- Email -->

            <div class="col-span-12">
                <div class="text-sm text-gray-500">
                    <span class="text-red-600">*&nbsp;</span
                    >{{ t('customerInformation.emailAddress') }}
                </div>
                <input-text
                    id="email"
                    v-model="email"
                    inputmode="email"
                    type="email"
                    autocomplete="email"
                    :placeholder="t('customerInformation.emailAddress')"
                    class="w-full"
                    :disabled="isProcessing"
                    :pt="{
                        root: {
                            class:
                                showErrors && errorEmail ? 'border-red-600' : ''
                        }
                    }"
                    :pt-options="{ mergeSections: true, mergeProps: true }"
                />
                <div
                    v-if="showErrors && errorEmail"
                    class="text-red-600 text-sm"
                >
                    {{ errorEmail }}
                </div>
            </div>

            <!-- Do Not Mail -->

            <div
                v-if="showDoNotMail"
                class="col-span-12"
            >
                <div class="ml-4 flex gap-2 pt-1">
                    <prime-checkbox
                        v-model="doNotMailComputed"
                        input-id="mailing_list_checkbox"
                        :binary="true"
                        :disabled="isProcessing"
                    />
                    <label for="mailing_list_checkbox">
                        {{ facilityData.doNotMailPromptText }}
                    </label>
                </div>
                <div class="text-sm">&nbsp;</div>
            </div>

            <!-- Mobile Phone -->

            <div class="col-span-12">
                <div class="text-sm text-gray-500">
                    {{ t('customerInformation.mobilePhone') }}
                </div>
                <phone-number
                    v-model="mobilePhone"
                    :initial-value="mobilePhoneInitial"
                    :placeholder="t('customerInformation.mobilePhone')"
                    :autofocus="false"
                    :disabled="isProcessing"
                />
                <div class="text-red-600 text-sm">
                    {{ errorMobilePhone || '&nbsp;' }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData, graphql } from '~/resources/graphql'
import { boolean, object, string } from 'yup'
import { toTypedSchema } from '@vee-validate/yup'
import type { TypedSchema } from 'vee-validate'
import {
    doNotMailChecked,
    doNotMailDefault,
    doNotMailInitialValue,
    doNotMailValue
} from '~/components/PurchaseContract/do-not-mail-helpers'

type TranslationFunction = (key: string, ...args: unknown[]) => string

export const CustomerInformationCustomerFragment = graphql(/* GraphQL */ `
    fragment CustomerInformationCustomerFragment on Customer {
        id
        firstName
        middleName
        lastName
        dateOfBirth
        email
        doNotMailStatus
        mobilePhone
    }
`)

export const CustomerInformationFacilityFragment = graphql(/* GraphQL */ `
    fragment CustomerInformationFacilityFragment on Facility {
        doNotMailDirection
        doNotMailDefaultChecked
        triStateDoNotMailEnabled
        hideDoNotMailForExistingOptedInCustomer
        doNotMailPromptText(language: $language)
    }
`)

export function CustomerInformationCheckoutFormSchema(t: TranslationFunction) {
    return object({
        enrollmentId: string(),
        firstName: string()
            .min(1, t('customerInformation.firstNameRequired'))
            .max(255)
            .required(t('customerInformation.firstNameRequired'))
            .label(t('customerInformation.firstName')),
        middleName: string()
            .max(255)
            .label(t('customerInformation.middleName')),
        lastName: string()
            .min(2, t('customerInformation.twoOrMoreCharactersRequired'))
            .max(255)
            .required(t('customerInformation.twoOrMoreCharactersRequired'))
            .label(t('customerInformation.lastName')),
        dateOfBirth: string().label(t('customerInformation.dateOfBirth')),
        mobilePhone: object()
            .shape({
                formatted: string(),
                number: string(),
                valid: boolean()
            })
            .test({
                message: t('customerInformation.invalidMobilePhoneWarning'),
                test: value =>
                    value.number === undefined ||
                    value.number === '' ||
                    value.valid === true
            }),
        email: string()
            .required(t('customerInformation.emailRequiredWarning'))
            .label(t('customerInformation.emailAddress'))
            .email(t('customerInformation.invalidEmailWarning')),
        doNotMail: boolean().nullable().label('Do not mail')
    })
}

const translationStub: TranslationFunction = () => ''

export function CustomerInformationCheckoutFormInitialValues(
    customer: FragmentType<typeof CustomerInformationCustomerFragment> | null,
    facility: FragmentType<typeof CustomerInformationFacilityFragment>,
    onlineGuestId: string
) {
    let customerData =
        customer === null
            ? null
            : getFragmentData(CustomerInformationCustomerFragment, customer)

    if (customerData?.id === onlineGuestId) {
        customerData = null
    }

    const facilityData = getFragmentData(
        CustomerInformationFacilityFragment,
        facility
    )

    return {
        firstName: customerData?.firstName ?? '',
        middleName: customerData?.middleName ?? '',
        lastName: customerData?.lastName ?? '',
        dateOfBirth: customerData?.dateOfBirth ?? '',
        email: customerData?.email ?? '',
        // Legacy mode always starts from the facility default; tri-state mode can preserve an explicit answer.
        doNotMail: doNotMailInitialValue(
            customerData?.doNotMailStatus ?? null,
            facilityData.doNotMailDirection,
            facilityData.doNotMailDefaultChecked,
            facilityData.triStateDoNotMailEnabled
        )
    }
}

export const customerInformationSchema = toTypedSchema(
    CustomerInformationCheckoutFormSchema(translationStub)
)
export type CustomerInformationSchemaType =
    typeof customerInformationSchema extends TypedSchema<unknown, infer TOutput>
        ? TOutput
        : never
</script>

<script setup lang="ts">
import InputText from 'primevue/inputtext'
import PrimeButton from 'primevue/button'
import PhoneNumber from '~/components/Input/PhoneNumber.vue'
import { useField } from 'vee-validate'
import {
    formatTimelessDate,
    parseTimeless
} from '~/composables/support/localization'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import PrimeCheckbox from 'primevue/checkbox'
import DropdownDateInput from '~/components/Input/DropdownDateInput.vue'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'
import { useI18n } from 'vue-i18n'
import { useActiveFacility } from '~/composables/use-active-facility'

const props = defineProps<{
    customer: FragmentType<typeof CustomerInformationCustomerFragment>
    facility: FragmentType<typeof CustomerInformationFacilityFragment>
    showErrors: boolean
    editing: boolean
    collectDoNotMail: boolean
    alwaysShowDoNotMail: boolean
    doNotMailVisible: boolean
}>()

const emit = defineEmits<{
    (e: 'update:editing', payload: boolean): void
    (e: 'update:doNotMailVisible', payload: boolean): void
}>()

const { t } = useI18n({ useScope: 'global' })
const activeFacility = useActiveFacility()
const customerData = computed(() =>
    getFragmentData(CustomerInformationCustomerFragment, props.customer)
)
const useMiddleName = computed(() => {
    // Existing customers can review their stored middle name even when new collection is disabled.
    return (
        activeFacility.value.organization.useMiddleName ||
        customerData.value.middleName !== ''
    )
})

const facilityData = computed(() =>
    getFragmentData(CustomerInformationFacilityFragment, props.facility)
)

onMounted(() => {
    if (customerData.value.email === '') {
        nextTick(() => document.getElementById('email')?.focus())
    }
})

const { value: firstName, errorMessage: errorFirstName } =
    useField('customer.firstName')
const { value: middleName, errorMessage: errorMiddleName } = useField(
    'customer.middleName'
)
const { value: lastName, errorMessage: errorLastName } =
    useField('customer.lastName')
const displayName = computed(() => {
    // Existing middle names remain visible without introducing gaps when any name part is empty.
    return [firstName.value, middleName.value, lastName.value]
        .filter(name => name !== '')
        .join(' ')
})
const { value: dateOfBirth, errorMessage: errorDateOfBirth } = useField(
    'customer.dateOfBirth'
)
const { value: email, errorMessage: errorEmail } = useField('customer.email')
const { value: mobilePhone, errorMessage: errorMobilePhone } = useField(
    'customer.mobilePhone'
)
const { value: doNotMail } = useField<boolean | null>('customer.doNotMail')
// Track direct checkbox interaction separately from form validation and programmatic default changes.
const doNotMailWasEdited = ref(false)

const showDoNotMail = computed(() => {
    // Plan eligibility is authoritative even if the customer edited a previously visible prompt.
    if (!props.collectDoNotMail) {
        return false
    }

    if (props.alwaysShowDoNotMail) {
        return true
    }

    // A customer-selected value must stay visible even if the plan override disappears.
    if (doNotMailWasEdited.value) {
        return true
    }

    if (
        customerData.value.id ===
        activeFacility.value.organization.onlineGuestId
    ) {
        return true
    }

    return (
        !facilityData.value.hideDoNotMailForExistingOptedInCustomer ||
        // Hide only an explicit subscription; unanswered customers still need the prompt.
        customerData.value.doNotMailStatus !== 'SUBSCRIBED' ||
        email.value === '' ||
        customerData.value.email.toLowerCase() !== email.value.toLowerCase()
    )
})

// Open every eligible prompt before checkout can submit its authoritative default.
emit('update:editing', customerData.value.email === '' || showDoNotMail.value)

watch(
    () => props.alwaysShowDoNotMail,
    (alwaysShowDoNotMail, previousAlwaysShowDoNotMail) => {
        if (alwaysShowDoNotMail === previousAlwaysShowDoNotMail) {
            return
        }

        if (alwaysShowDoNotMail) {
            // A newly-required prompt must be reopened even when the rest of the form was collapsed.
            emit('update:editing', true)
        }
    }
)

watch(
    showDoNotMail,
    (doNotMailVisible, previousDoNotMailVisible) => {
        if (doNotMailVisible && previousDoNotMailVisible === false) {
            // Email and cart changes can make a previously hidden prompt eligible during checkout.
            emit('update:editing', true)
        }

        // Parent mutation builders omit consent whenever the customer cannot review the checkbox.
        if (props.doNotMailVisible !== doNotMailVisible) {
            emit('update:doNotMailVisible', doNotMailVisible)
        }
    },
    { immediate: true }
)

const doNotMailComputed = computed({
    get() {
        return doNotMailChecked(
            facilityData.value.doNotMailDirection,
            doNotMail.value ??
                doNotMailDefault(
                    facilityData.value.doNotMailDirection,
                    facilityData.value.doNotMailDefaultChecked
                )
        )
    },
    set(newValue) {
        // This setter is reached only through direct checkbox interaction.
        doNotMailWasEdited.value = true
        doNotMail.value = doNotMailValue(
            facilityData.value.doNotMailDirection,
            newValue
        )
    }
})

const mobilePhoneInitial = computed<string>(() => {
    if (
        typeof mobilePhone.value.number === 'string' &&
        mobilePhone.value.number !== ''
    ) {
        return mobilePhone.value.number
    }

    return customerData.value.mobilePhone
})

watch(
    [
        errorFirstName,
        errorMiddleName,
        errorLastName,
        errorDateOfBirth,
        errorEmail,
        errorMobilePhone
    ],
    ([
        newValueFirstName,
        newValueMiddleName,
        newValueLastName,
        newValueDateOfBirth,
        newValueEmail,
        newValueMobilePhone
    ]) => {
        if (
            newValueFirstName ||
            newValueMiddleName ||
            newValueLastName ||
            newValueDateOfBirth ||
            newValueEmail ||
            newValueMobilePhone
        ) {
            emit('update:editing', true)
        }
    }
)

const { isProcessing } = useCheckoutFormProcessing()
</script>
